import type { AdminDashboard } from '@practium/features/api';
import type { DashboardConfig } from '@practium/features/dashboard';
import {
	adminActivityLineConfig,
	adminActivityLineData,
	adminPlansConfig,
	adminPlansData,
	adminSubscriptionsChartConfig,
	adminSubscriptionsChartData,
	adminUserTypesDonutConfig,
	adminUserTypesDonutData,
	adminUsersChartConfig,
	adminUsersChartData,
} from './adminCharts.config';

const getPlanId = (item: Record<string, unknown>, index: number) => {
	const id = item.planId ?? item.id ?? item.position ?? index + 1;

	return typeof id === 'string' || typeof id === 'number' ? id : index + 1;
};

const getPlanName = (item: Record<string, unknown>) => {
	const name = item.name ?? item.label;

	return typeof name === 'string' ? name : 'Plano';
};

const getPlanValue = (item: Record<string, unknown>) => {
	const value = item.subscriptions ?? item.activeSubscriptions ?? item.value;

	if (typeof value === 'number') {
		return `${value} assinaturas`;
	}

	if (typeof value === 'string') {
		return value;
	}

	return undefined;
};

export const getAdminCharts = (data?: AdminDashboard): DashboardConfig['slots'] => [
	{
		type: 'comparisonBar',
		size: 'twoThirds',
		config: {
			...adminUsersChartConfig,
			mainValue: data?.monthlyChart?.title ?? adminUsersChartConfig.mainValue,
			footerText: data?.monthlyChart?.subtitle ?? adminUsersChartConfig.footerText,
			trendValue: `${data?.monthlyChart?.growthPercentage}%`,
			trendDirection: (data?.monthlyChart?.growthPercentage ?? 0) > 0 ? 'up' : 'down',
		},
		data:
			data?.monthlyChart?.data.map((item) => ({
				month: item.month,
				realizado: item.realized,
				planejado: item.realizedLastYear,
			})) ?? adminUsersChartData,
	},
	{
		type: 'donut',
		size: 'oneThird',
		config: {
			...adminUserTypesDonutConfig,
			title: data?.doughnutCharts?.[0]?.title ?? adminUserTypesDonutConfig.title,
			subtitle: data?.doughnutCharts?.[0]?.subtitle ?? adminUserTypesDonutConfig.subtitle,
		},
		data: data?.doughnutCharts?.[0]?.items.map((item) => ({
			name: item.label,
			value: item.value,
			percent: item.percent,
		})) ?? adminUserTypesDonutData,
	},
	{
		type: 'pie',
		size: 'oneThird',
		config: {
			...adminSubscriptionsChartConfig,
			title: data?.doughnutCharts?.[1]?.title ?? adminSubscriptionsChartConfig.title,
			subtitle: data?.doughnutCharts?.[1]?.subtitle ?? adminSubscriptionsChartConfig.subtitle,
		},
		data: data?.doughnutCharts?.[1]?.items.map((item) => ({
			name: item.label,
			value: item.value,
			percent: item.percent,
		})) ?? adminSubscriptionsChartData,
	},
	{
		type: 'metricList',
		size: 'oneThird',
		config: {
			...adminPlansConfig,
			title: data?.lists?.[0]?.title ?? adminPlansConfig.title,
			subtitle: data?.lists?.[0]?.subtitle ?? adminPlansConfig.subtitle,
		},
		data: data?.lists?.[0]?.items.map((item, index) => {
			const plan = item as Record<string, unknown>;

			return {
				id: getPlanId(plan, index),
				name: getPlanName(plan),
				value: getPlanValue(plan),
			};
		}) ?? adminPlansData,
	},
	{
		type: 'comparisonLine',
		size: 'oneThird',
		config: {
			...adminActivityLineConfig,
			title: data?.lineCharts?.[0]?.title ?? adminActivityLineConfig.title,
			mainValue: data?.lineCharts?.[0]?.subtitle,
			trendValue: data?.lineCharts?.[0]?.growthPercentage
				? `${data.lineCharts[0].growthPercentage}%`
				: adminActivityLineConfig.trendValue,
			footerText: data?.lineCharts?.[0]?.period ?? adminActivityLineConfig.footerText,
			trendDirection: data?.lineCharts?.[0]?.growthPercentage
				? data.lineCharts[0].growthPercentage >= 0
					? 'up'
					: 'down'
				: adminActivityLineConfig.trendDirection,
		},
		data:
			data?.lineCharts?.[0]?.data.map((item) => ({
				day: item.label,
				current: item.current,
				previous: item.previous,
			})) ?? adminActivityLineData,
	},
];
